<template>
  <v-container>
    <h2>Update Profile Information</h2>
    <v-form @submit.prevent="submitForm">
      <v-card class="pa-4">
        <v-text-field
          v-model="userInfo.email"
          label="Email Address"
          variant="outlined"
          type="email"
          :rules="[
            (v) => !!v || 'Email is required',
            (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
          ]"
        ></v-text-field>

        <v-text-field
          v-model="userInfo.firstName"
          label="First Name"
          variant="outlined"
          :rules="[(v) => !!v || 'First name is required']"
        ></v-text-field>

        <v-text-field
          v-model="userInfo.lastName"
          label="Last Name"
          variant="outlined"
          :rules="[(v) => !!v || 'Last name is required']"
        ></v-text-field>

        <v-divider class="my-4"></v-divider>

        <h3 class="mb-3">Change Password (Optional)</h3>

        <v-text-field
          v-model="passwordChange.currentPassword"
          label="Current Password"
          variant="outlined"
          type="password"
          :rules="
            passwordChange.newPassword
              ? [(v) => !!v || 'Current password is required to change password']
              : []
          "
        ></v-text-field>

        <v-text-field
          v-model="passwordChange.newPassword"
          label="New Password"
          variant="outlined"
          type="password"
          :rules="
            passwordChange.newPassword
              ? [
                  (v) => v.length >= 6 || 'Password must be at least 6 characters',
                  (v) =>
                    /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(v) ||
                    'Password must contain uppercase, lowercase, and number',
                ]
              : []
          "
        ></v-text-field>

        <v-text-field
          v-model="passwordChange.confirmPassword"
          label="Confirm New Password"
          variant="outlined"
          type="password"
          :rules="
            passwordChange.newPassword
              ? [
                  (v) => !!v || 'Please confirm your password',
                  (v) => v === passwordChange.newPassword || 'Passwords do not match',
                ]
              : []
          "
        ></v-text-field>

        <v-alert
          v-if="errorMessage"
          type="error"
          dismissible
          class="mt-2"
          @click:close="errorMessage = ''"
        >
          {{ errorMessage }}
        </v-alert>

        <v-alert
          v-if="successMessage"
          type="success"
          dismissible
          class="mt-2"
          @click:close="successMessage = ''"
        >
          {{ successMessage }}
        </v-alert>

        <v-card-actions class="justify-end">
          <v-btn variant="outlined" @click="resetForm" :disabled="loading"> Reset </v-btn>
          <v-btn color="primary" type="submit" :loading="loading"> Update User Information </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();
const route = useRoute();
const loading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const userInfo = ref({
  email: '',
  firstName: '',
  lastName: '',
});

const passwordChange = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const originalUserInfo = ref({});

const loadUserInfo = async () => {
  try {
    const response = await axios.get(`/api/profile`);
    userInfo.value = { ...response.data };
    originalUserInfo.value = { ...response.data };
  } catch (error) {
    console.error('Error loading user info:', error);
    errorMessage.value = 'Failed to load user information.';
  }
};

await loadUserInfo();


const resetForm = () => {
  userInfo.value = { ...originalUserInfo.value };
  passwordChange.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };
  errorMessage.value = '';
  successMessage.value = '';
};

const submitForm = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';
    successMessage.value = '';

    const updateData = { ...userInfo.value };

    // Add password change data if provided
    if (passwordChange.value.newPassword) {
      updateData.currentPassword = passwordChange.value.currentPassword;
      updateData.newPassword = passwordChange.value.newPassword;
    }

    const userId = route.params.id || 'current';
    await axios.put(`/api/profile`, updateData);

    successMessage.value = 'User information updated successfully!';

    // Clear password fields after successful update
    passwordChange.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    // Update original data for reset functionality
    originalUserInfo.value = { ...userInfo.value };

    // Redirect after a short delay if not editing current user
    if (route.params.id && route.params.id !== 'current') {
      setTimeout(() => {
        router.push({ name: 'Move…' });
      }, 2000);
    }
  } catch (error) {
    console.error('Error updating user:', error);

    // Handle different error scenarios
    if (error.response?.status === 400) {
      const fullMessage =
        error.response.data?.message || 'Invalid input data. Please check your entries.';
      const problemIndex = fullMessage.indexOf('problem: ');
      errorMessage.value =
        problemIndex !== -1 ? fullMessage.substring(problemIndex + 9) : fullMessage;
    } else if (error.response?.status === 401) {
      errorMessage.value = 'Unauthorized. Please check your current password.';
    } else if (error.response?.status === 403) {
      errorMessage.value = 'You do not have permission to update this user.';
    } else if (error.response?.status === 500) {
      errorMessage.value = 'Server error. Please try again later.';
    } else if (error.response) {
      errorMessage.value = error.response.data?.message || `Error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage.value = 'Network error. Please check your connection and try again.';
    } else {
      errorMessage.value = 'An unexpected error occurred. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>
